import { API_URL } from '../utils/api';
import { clearSession, getToken } from './authStorage';

interface ApiGetOptions {
  bypassCache?: boolean;
}

function authHeaders(): Record<string, string> {
  const token = getToken();
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

const PUBLIC_PATHS = ['/login', '/createAccount'];

async function expectOk(response: Response, label: string): Promise<void> {
  if (response.status === 401 && !PUBLIC_PATHS.includes(window.location.pathname)) {
    clearSession();
    window.location.href = '/login';
    throw new Error('Session expired. Redirecting to login.');
  }
  if (!response.ok) {
    throw new Error(`${label} failed with status ${response.status}`);
  }
}

export async function apiGet<T>(
  path: string,
  useAuth = true,
  options: ApiGetOptions = {}
): Promise<T> {
  const headers = useAuth
    ? authHeaders()
    : { 'Content-Type': 'application/json' };
  const requestInit: RequestInit = { method: 'GET', headers };
  if (options.bypassCache) {
    requestInit.cache = 'no-store';
  }

  const response = await fetch(API_URL + path, requestInit);
  await expectOk(response, `GET ${path}`);
  return response.json();
}

export async function apiPost<T>(
  path: string,
  body: unknown,
  useAuth = true
): Promise<T> {
  const headers = useAuth
    ? authHeaders()
    : { 'Content-Type': 'application/json' };
  const response = await fetch(API_URL + path, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });
  await expectOk(response, `POST ${path}`);
  return response.json();
}

export async function apiPostVoid(
  path: string,
  body: unknown,
  useAuth = true
): Promise<void> {
  const headers = useAuth
    ? authHeaders()
    : { 'Content-Type': 'application/json' };
  const response = await fetch(API_URL + path, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });
  await expectOk(response, `POST ${path}`);
}

export async function apiPut(path: string, body: unknown): Promise<void> {
  const response = await fetch(API_URL + path, {
    method: 'PUT',
    headers: authHeaders(),
    body: JSON.stringify(body),
  });
  await expectOk(response, `PUT ${path}`);
}

export async function apiDelete(path: string, body?: unknown): Promise<void> {
  const response = await fetch(API_URL + path, {
    method: 'DELETE',
    headers: authHeaders(),
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  await expectOk(response, `DELETE ${path}`);
}

export async function apiPostFormData<T>(path: string, formData: FormData): Promise<T> {
  const token = getToken();
  const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};
  const response = await fetch(API_URL + path, {
    method: 'POST',
    headers,
    body: formData,
  });
  await expectOk(response, `POST ${path}`);
  return response.json();
}
